import { ActivityMode } from '@/models/Session';

import { defineStore } from 'pinia';
import { RouteParams, useRoute, useRouter } from 'vue-router';
import { computed } from 'vue';

import { GHRoutes } from '@/router/routes';

export const useNavigationStore = defineStore('navigation', () => {
  const route = useRoute();
  const router = useRouter();
  const { contentType, slug } = route.params;

  const baseParams = computed((): RouteParams => ({ contentType, slug }));

  const createRouteParams = (additionalParams?: RouteParams) => ({
    ...baseParams.value,
    ...additionalParams,
  });

  const navigateTo = (method: 'push' | 'replace', name: string, additionalParams?: RouteParams) => {
    router[method]({ name, params: createRouteParams(additionalParams) });
  };

  const removeQueryParams = () => {
    router.replace({ query: undefined });
  };

  return {
    redirectToContentPage: () => navigateTo('replace', GHRoutes.ContentPage),
    redirectToNewContentPage: (slug: string) =>
      navigateTo('replace', GHRoutes.ContentPage, { slug }),
    redirectToContentTeamUpPage: () => navigateTo('push', GHRoutes.ContentTeamUpPage),
    redirectToContentTeamUpStartPage: () => navigateTo('push', GHRoutes.ContentTeamUpStartPage),
    redirectToLessonGuidePage: () => navigateTo('push', GHRoutes.LessonGuidePage),
    redirectToLessonStepPage: (stepId: string) =>
      navigateTo('push', GHRoutes.LessonStepPage, { stepId }),
    redirectToLessonAudioPage: () => navigateTo('push', GHRoutes.LessonAudioPage),
    redirectToLessonTakeawaysPage: () => navigateTo('push', GHRoutes.LessonTakeawaysPage),
    redirectToLessonFeedbackPage: () => navigateTo('push', GHRoutes.LessonFeedbackPage),
    redirectToLessonGroupExercisePage: () => navigateTo('push', GHRoutes.LessonGroupExercisePage),
    redirectToNotFoundPage: () => navigateTo('push', GHRoutes.NotFound),
    redirectToInternalErrorPage: () => navigateTo('push', GHRoutes.InternalError),
    redirectToLearningActivityPage: (slug: string) =>
      navigateTo('replace', GHRoutes.LearningActivityPage, { slug }),
    redirectToActivityModeRedirectPage: (courseCode: string, activityMode: ActivityMode) =>
      navigateTo('replace', GHRoutes.ActivityModeRedirectPage, {
        activityId: courseCode,
        activityMode,
      }),
    redirectToActivityPage: (courseCode: string, isGhccActivity: boolean) =>
      navigateTo(
        'replace',
        isGhccActivity ? GHRoutes.GhccActivityLoadingPage : GHRoutes.GhActivityPage,
        {
          activityId: courseCode,
        },
      ),
    removeQueryParams,
  };
});
